import React, { useEffect, useMemo, useState } from "react";
import Icon from "../../components/ui/Icon";
import DialogModal from "../../components/overlays/DialogModal";

export default function AddChannelDialog({ isOpen, nodes = [], onClose, onConfirm }) {
  const ewlNodes = useMemo(() => nodes.filter((n) => n.subtype === "EWL-810"), [nodes]);
  const [mode, setMode] = useState("auto");
  const [selection, setSelection] = useState({});
  const [aggregate, setAggregate] = useState(false);
  const [pendingReassign, setPendingReassign] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setMode("auto");
      setSelection({});
      setAggregate(false);
      setPendingReassign(null);
    }
  }, [isOpen]);

  const hasFreeSlots = ewlNodes.some((n) => (n.channels || []).some((ch) => !ch.ownerDeviceId));

  return (
    <>
    <DialogModal isOpen={isOpen} position="absolute">
      <div className="w-full rounded-2xl border border-brand-purple bg-brand-navy p-4 space-y-4 shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="add" className="text-brand-magenta" />
            <span className="text-sm font-semibold text-brand-light">Add channel(s)</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-purple/70 text-brand-light hover:bg-brand-purple transition"
          >
            ×
          </button>
        </div>

        <div className="space-y-2">
          <span className="text-xs font-semibold text-brand-light/70">Mode</span>
          <div className="grid grid-cols-2 gap-2">
            {[
              { id: "auto", label: "Use next free slot" },
              { id: "manual", label: "Select EWL channels" },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setMode(opt.id)}
                className={`rounded-lg px-3 py-2 text-sm font-semibold ${
                  mode === opt.id ? "bg-brand-magenta text-white" : "bg-brand-purple text-brand-light"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {mode === "manual" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border border-brand-navy bg-brand-purple px-3 py-2">
              <div className="flex items-center gap-2 text-sm text-brand-light">
                <Icon name="merge_type" className="text-brand-magenta" />
                <span>Create single device for selected channels</span>
              </div>
              <label className="flex items-center gap-2 text-xs text-brand-light">
                <input
                  type="checkbox"
                  checked={aggregate}
                  onChange={(e) => setAggregate(e.target.checked)}
                  className="accent-brand-magenta"
                />
                <span>Aggregate</span>
              </label>
            </div>
            {ewlNodes.map((n) => {
              const free = (n.channels || []).filter((ch) => !ch.ownerDeviceId);
              const inUseCount = (n.channels || []).filter((ch) => ch.ownerDeviceId).length;
              const selected = selection[n.id] || [];
              return (
                <div key={n.id} className="space-y-2 rounded-xl border border-brand-navy bg-brand-purple p-3">
                  <div className="flex items-center justify-between text-sm text-brand-light">
                    <span>{n.name || n.id}</span>
                    <span className="text-[11px] text-brand-light/50">{free.length} free</span>
                  </div>
                  {inUseCount > 0 && (
                    <div className="text-[11px] text-amber-300">
                      {inUseCount} channel(s) already mapped.
                    </div>
                  )}
                  <div className="grid grid-cols-4 gap-2">
                    {(n.channels || []).map((ch) => {
                      const active = selected.includes(ch.index);
                      const isFree = !ch.ownerDeviceId;
                      return (
                        <button
                          key={ch.index}
                          type="button"
                          onClick={() => {
                            if (isFree) {
                              setSelection((prev) => {
                                const arr = prev[n.id] || [];
                                const next = active
                                  ? arr.filter((i) => i !== ch.index)
                                  : [...arr, ch.index];
                                return { ...prev, [n.id]: next };
                              });
                            } else {
                              setPendingReassign({
                                nodeId: n.id,
                                nodeName: n.name,
                                index: ch.index,
                                current: ch.ownerDeviceId,
                              });
                            }
                          }}
                          className={`rounded-lg px-3 py-1 text-xs font-semibold border ${
                            active
                              ? "bg-brand-magenta text-white border-brand-magenta"
                              : isFree
                              ? "bg-brand-purple text-brand-light border-brand-purple/70"
                              : "bg-[#2a1b14] text-amber-200 border-amber-500/60"
                          }`}
                          title={!isFree ? `In use by ${ch.ownerDeviceId}` : undefined}
                        >
                          Ch {ch.index}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!hasFreeSlots && (
          <div className="rounded-lg border border-amber-500/50 bg-[#2b1f12] px-3 py-2 text-xs text-amber-200">
            No free EWL channels available.
          </div>
        )}

        <div className="flex flex-col gap-2">
          <button
            type="button"
            disabled={!hasFreeSlots}
            onClick={() => {
              const selections =
                mode === "manual"
                  ? Object.entries(selection).map(([nodeId, indices]) => ({ nodeId, indices }))
                  : [];
              onConfirm({ mode, selections, aggregate });
            }}
            className={`w-full rounded-xl px-4 py-2 text-sm font-semibold shadow transition ${
              hasFreeSlots
                ? "bg-brand-magenta text-white hover:bg-brand-pink"
                : "bg-brand-purple/40 text-brand-light/40 cursor-not-allowed"
            }`}
          >
            Add channel{mode === "manual" ? "(s)" : ""}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl border border-brand-purple bg-brand-purple px-4 py-2 text-sm font-semibold text-brand-light hover:border-brand-magenta transition"
          >
            Cancel
          </button>
        </div>
      </div>

    </DialogModal>
    <DialogModal isOpen={!!pendingReassign} position="absolute">
          <div className="w-full rounded-2xl border border-brand-purple bg-brand-navy p-4 space-y-3 shadow-xl">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/20 text-amber-300">
                <Icon name="warning" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-brand-light">Channel already mapped</span>
                <span className="text-[11px] text-brand-light/50">
                  Release channel {pendingReassign?.index} on{" "}
                  {pendingReassign?.nodeName || pendingReassign?.nodeId} from{" "}
                  {pendingReassign?.current}?
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setSelection((prev) => {
                    const arr = prev[pendingReassign?.nodeId] || [];
                    const next = arr.includes(pendingReassign?.index)
                      ? arr
                      : [...arr, pendingReassign?.index];
                    return { ...prev, [pendingReassign?.nodeId]: next };
                  });
                  setPendingReassign(null);
                }}
                className="w-full rounded-xl bg-brand-magenta px-4 py-2 text-sm font-semibold text-white hover:bg-brand-pink transition"
              >
                Release &amp; Select
              </button>
              <button
                type="button"
                onClick={() => setPendingReassign(null)}
                className="w-full rounded-xl border border-brand-purple bg-brand-purple px-4 py-2 text-sm font-semibold text-brand-light hover:border-brand-magenta transition"
              >
                Cancel
              </button>
            </div>
          </div>
    </DialogModal>
    </>
  );
}
